import React, { useEffect, useRef, useState } from "react";
import { ShieldAlert, Phone, X, CheckCircle2 } from "lucide-react";
import { useClara } from "../contexts/ClaraContext";
import { useData } from "../contexts/DataContext";

const CONFIRM_SECONDS = 5;

export const EmergencyCard = () => {
    const { speak } = useClara();
    const { primaryContact, alertContact } = useData();
    const primaryName = primaryContact?.name || "Tom";
    const alertName = alertContact?.name || "Jenny";
    const [stage, setStage] = useState("idle"); // idle | confirming | sent
    const [remaining, setRemaining] = useState(CONFIRM_SECONDS);
    const timerRef = useRef(null);
    const sentTimerRef = useRef(null);

    const clearTimers = () => {
        if (timerRef.current) clearInterval(timerRef.current);
        if (sentTimerRef.current) clearTimeout(sentTimerRef.current);
        timerRef.current = null;
        sentTimerRef.current = null;
    };

    useEffect(() => () => clearTimers(), []);

    // Auto-cancel side-effect — runs as effect (NOT inside state updater)
    useEffect(() => {
        if (stage === "confirming" && remaining === 0) {
            clearTimers();
            setStage("idle");
            speak("Emergency cancelled. No call was placed.");
        }
    }, [stage, remaining, speak]);    const startConfirm = () => {
        if (stage === "confirming") return;
        speak(
            `Emergency help. Tap again within ${CONFIRM_SECONDS} seconds to call ${primaryName} and share your location with ${alertName}. Tap cancel to stop.`,
        );
        setStage("confirming");
        setRemaining(CONFIRM_SECONDS);
        clearTimers();
        timerRef.current = setInterval(() => {
            setRemaining((s) => (s > 0 ? s - 1 : 0));
        }, 1000);
    };

    const confirmCall = () => {
        clearTimers();
        setStage("sent");
        speak(
            `Calling ${primaryName} now. Sharing your location with ${alertName}. Help is on the way, Maria.`,
        );
        sentTimerRef.current = setTimeout(() => {
            setStage("idle");
        }, 6000);
    };

    const cancel = () => {
        clearTimers();
        setStage("idle");
        speak("Emergency cancelled. No call was placed.");
    };

    if (stage === "sent") {
        return (
            <section
                role="status"
                aria-live="assertive"
                data-testid="emergency-sent"
                className="rounded-3xl border-2 border-[#0A5C36] bg-[#E5F1EB] p-5 shadow-[0_4px_20px_rgba(10,92,54,0.18)] fade-in-up"
            >
                <div className="flex items-start gap-3">
                    <span
                        aria-hidden="true"
                        className="shrink-0 w-12 h-12 rounded-2xl bg-[#0A5C36] text-white flex items-center justify-center"
                    >
                        <CheckCircle2 className="w-6 h-6" />
                    </span>
                    <div className="flex-1">
                        <p className="font-display text-lg font-bold text-[#0A5C36] leading-tight">
                            Calling {primaryName} now
                        </p>
                        <p className="text-sm text-[#1A1A2E] mt-0.5 leading-snug">
                            Sharing your location with {alertName}. Help is on
                            the way.
                        </p>
                    </div>
                </div>
            </section>
        );
    }

    if (stage === "confirming") {
        return (
            <section
                role="alertdialog"
                aria-modal="false"
                aria-label="Confirm emergency call"
                data-testid="emergency-confirming"
                className="rounded-3xl border-2 border-[#A31621] bg-[#FFF1F2] p-5 shadow-[0_4px_24px_rgba(163,22,33,0.22)] fade-in-up"
            >
                <div className="flex items-start gap-3">
                    <span
                        aria-hidden="true"
                        className="relative shrink-0 w-12 h-12 rounded-2xl bg-[#A31621] text-white flex items-center justify-center"
                    >
                        <span className="absolute inset-0 rounded-2xl bg-[#A31621]/50 pulse-ring" />
                        <ShieldAlert className="relative w-6 h-6" />
                    </span>
                    <div className="flex-1 min-w-0">
                        <p className="font-display text-lg font-bold text-[#A31621] leading-tight">
                            Tap to call {primaryName}
                        </p>
                        <p className="text-sm text-[#1A1A2E] mt-0.5 leading-snug">
                            Auto-cancels in{" "}
                            <span
                                className="font-bold tabular-nums"
                                data-testid="emergency-countdown"
                            >
                                {remaining}s
                            </span>
                            . {alertName} will be alerted too.
                        </p>
                    </div>
                </div>
                <div className="mt-4 flex gap-2">
                    <button
                        type="button"
                        onClick={confirmCall}
                        data-testid="emergency-confirm-btn"
                        aria-label={`Confirm: call ${primaryName} now`}
                        className="flex-1 min-h-[56px] rounded-2xl bg-[#A31621] hover:bg-[#7c101a] text-white text-lg font-bold flex items-center justify-center gap-2 transition-colors shadow-sm"
                    >
                        <Phone className="w-5 h-5" aria-hidden="true" />
                        Call {primaryName} now
                    </button>
                    <button
                        type="button"
                        onClick={cancel}
                        data-testid="emergency-cancel-btn"
                        aria-label="Cancel emergency call"
                        className="px-4 min-h-[56px] rounded-2xl border-2 border-[#A31621] text-[#A31621] font-bold hover:bg-[#A31621]/5 transition-colors"
                    >
                        <X className="w-5 h-5" aria-hidden="true" />
                    </button>
                </div>
            </section>
        );
    }

    return (
        <button
            type="button"
            onClick={startConfirm}
            aria-label={`Emergency. Tap to call ${primaryName} and alert ${alertName}.`}
            data-testid="emergency-card-btn"
            className="w-full text-left rounded-3xl bg-white border-l-4 border-[#A31621] shadow-[0_2px_14px_rgba(163,22,33,0.10)] hover:shadow-[0_8px_24px_rgba(163,22,33,0.18)] hover:-translate-y-0.5 transition-all p-5 flex items-center gap-4 min-h-[88px]"
        >
            <span
                aria-hidden="true"
                className="shrink-0 w-12 h-12 rounded-2xl bg-[#A31621] text-white flex items-center justify-center"
            >
                <ShieldAlert className="w-6 h-6" strokeWidth={2.3} />
            </span>
            <span className="flex-1 min-w-0">
                <span className="block font-display font-bold text-lg text-[#1A1A2E] leading-tight">
                    Emergency Help
                </span>
                <span className="block text-sm text-[#5A5A6E] mt-0.5 leading-snug">
                    One tap calls {primaryName} · alerts {alertName} with
                    location
                </span>
            </span>
            <span
                aria-hidden="true"
                className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[#FFF1F2] text-[#A31621]"
            >
                <Phone className="w-5 h-5" />
            </span>
        </button>
    );
};

export default EmergencyCard;
